<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { ref } from "vue";
import { z } from "zod";

import { useSendPasswordRecoveryEmailMutation } from "@/composables/kili/password-recovery/mutations/use-send-password-recovery-email-mutation";
import { useLoginMutation } from "@/composables/kili/security/mutations/use-login-mutation";
import { useLayout } from "@/composables/layout/use-layout";
import { useToaster } from "@/composables/layout/use-toaster";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import KiliApi from "@/data/api/kili/kili-api";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { useCurrentUserStore } from "@/stores/current-user/current-user-store";

import InputText from "primevue/inputtext";
import Password from "primevue/password";

import ForgotPasswordDialog from "@/components/authentication/forgot-password-dialog.vue";
import SelectLocale from "@/components/layout/select-locale.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const { t, k, rawT } = useTypedIntl();
const toast = useToaster();
const { isGlobalLoading } = useLayout();

const currentUserStore = useCurrentUserStore();

const { mutateAsync: login, isPending: isLoginPending } = useLoginMutation();
const { mutateAsync: sendPasswordRecoveryEmail } = useSendPasswordRecoveryEmailMutation();

const isForgotPasswordDialogVisible = ref<boolean>(false);

const { handleSubmit, errors, defineField, resetForm } = useForm({
  validationSchema: toTypedSchema(
    z.object({
      email: z.string().min(1, "required"),
      password: z.string().min(1, "required"),
    }),
  ),
  initialValues: {
    email: "",
    password: "",
  },
});

const [email] = defineField("email");
const [password] = defineField("password");

const submit = handleSubmit(async (values) => {
  try {
    await login({ username: values.email, password: values.password });
    const loggedUser = await KiliApi.getCurrentUser();

    await currentUserStore.login(loggedUser);

    await router.push({ name: ERouteName.PILOTING });
  } catch (e) {
    toastKiliError(e, "authentication.error");
    resetForm({
      values: {
        ...values,
        password: "",
      },
    });
  }
});

async function handleSendPasswordRecoveryEmail(email: string) {
  try {
    isGlobalLoading.value = true;
    await sendPasswordRecoveryEmail(email);

    toast.add({
      severity: "success",
      detail: t(k.authentication.forgotPassword.success),
    });

    MixpanelService.trackEvent(EMixpanelEvent.FORGOTTEN_PASSWORD_SUBMITTED);
  } catch (e) {
    toastKiliError(e);
  } finally {
    isForgotPasswordDialogVisible.value = false;
    isGlobalLoading.value = false;
  }
}
</script>

<template>
  <div class="flex flex-row-reverse p-4">
    <SelectLocale />
  </div>

  <div class="flex justify-center py-4">
    <form
      v-focustrap
      class="flex flex-col gap-4 w-80"
      @submit="submit"
    >
      <div class="flex justify-center items-center gap-2">
        <img
          class="w-6"
          src="/voltalis-icon.svg"
        />
        <span class="font-semibold">Voltalis</span>
      </div>
      <h3 class="mt-8">
        {{ t(k.authentication.title.welcome) }}
      </h3>
      <span class="text-text-secondary">{{ t(k.authentication.title.subtitle) }}</span>

      <div class="flex flex-col gap-2 mt-6">
        <label class="font-medium">{{ t(k.authentication.email.label) }}</label>
        <InputText
          v-model="email"
          autofocus
          data-test-id="username-input"
          :invalid="!!errors.email"
          :placeholder="t(k.authentication.email.placeholder)"
          type="text"
        />
        <small
          v-if="errors.email"
          class="text-red-500"
        >
          {{ rawT(`veeValidate.messages.${errors.email}`) }}
        </small>
      </div>

      <div class="flex flex-col gap-2">
        <label class="font-medium">{{ t(k.authentication.password.label) }}</label>
        <Password
          v-model="password"
          data-test-id="password-input"
          :feedback="false"
          :invalid="!!errors.password"
          :placeholder="t(k.authentication.password.placeholder)"
          toggle-mask
        />
        <small
          v-if="errors.password"
          class="text-red-500"
        >
          {{ rawT(`veeValidate.messages.${errors.password}`) }}
        </small>
      </div>

      <HospitalityButton
        data-test-id="forgot-password-link"
        severity="link"
        size="xs"
        @click="isForgotPasswordDialogVisible = true"
      >
        {{ t(k.authentication.forgotPassword.link) }}
      </HospitalityButton>

      <HospitalityButton
        class="mt-2"
        data-test-id="submit-login-button"
        :label="t(k.authentication.login)"
        :loading="isLoginPending"
        type="submit"
      />
    </form>
  </div>

  <ForgotPasswordDialog
    v-model="isForgotPasswordDialogVisible"
    @submit="handleSendPasswordRecoveryEmail($event)"
  />
</template>
